// CatchAllViewTypeButtons.jsx

import React from 'react';

// Device Detection
import { useIsCompactSplit, useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { getDefaultViewTypeIconKey, getDefaultViewTypeLabel } from 'utilities/ViewTypeIcons';
import { DEFAULT_CATCH_ALL_VIEW_TYPES } from 'views/utils/organicViews';

// CSS
import './CatchAllViewTypeButtons.css';

// Icons
import Icon from 'svg/Icon';

// Components
import PopupOption from 'components/popup/option/PopupOption';
import Popup from 'components/popup/Popup';
import CatchAllViewTypeButton from 'views/catchall/CatchAllViewTypeButton';

export { DEFAULT_CATCH_ALL_VIEW_TYPES };

const normalizeType = (type) => String(type || '').trim().toUpperCase();
const toDisplayLabel = (type) => getDefaultViewTypeLabel(type, '');
const toContextualViewTitle = (type, viewTitlePrefix = '') => {
    const normalizedPrefix = String(viewTitlePrefix || '').trim();
    const displayLabel = toDisplayLabel(type);

    if (!normalizedPrefix || !displayLabel) return '';

    return `${normalizedPrefix} - ${displayLabel} View`;
};
const TABLE_VIEW_TYPE = 'TABLE';

/**
 * CatchAllViewTypeButtons
 *
 * @returns {ReactNode} The rendered component.
 */
const CatchAllViewTypeButtons = React.memo(({
    theme,
    selectedType = null,
    onSelectType,
    types = DEFAULT_CATCH_ALL_VIEW_TYPES,
    forceCompact = false,
    viewTitlePrefix = '',
}) => {

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();
    const isCompactSplit = useIsCompactSplit();

    // ----- REFS ----- //

    const anchorRef = React.useRef(null);

    // ----- STATE ----- //

    const [popupOpen, setPopupOpen] = React.useState(false);
    const [isMobileTriggerHovered, setIsMobileTriggerHovered] = React.useState(false);

    // ----- MEMOS ----- //

    const normalizedTypes = React.useMemo(() => {
        const uniqueTypes = [];
        const seen = new Set();

        (Array.isArray(types) ? types : []).forEach((type) => {
            const normalizedType = normalizeType(type);
            if (!normalizedType || seen.has(normalizedType)) return;
            seen.add(normalizedType);
            uniqueTypes.push(normalizedType);
        });

        return uniqueTypes;
    }, [types]);

    const normalizedSelectedType = normalizeType(selectedType);
    const hasSelectedType = normalizedTypes.includes(normalizedSelectedType);
    const mobileVisibleType = hasSelectedType ? normalizedSelectedType : TABLE_VIEW_TYPE;
    const mobileVisibleIconKey = getDefaultViewTypeIconKey(mobileVisibleType, 'table');
    const shouldRenderCompactSelector = isMobile || isCompactSplit || forceCompact;

    // ----- EFFECTS ----- //

    React.useEffect(() => {
        if (!shouldRenderCompactSelector && popupOpen) {
            setPopupOpen(false);
        }
    }, [popupOpen, shouldRenderCompactSelector]);

    // ----- HANDLERS ----- //

    /**
 * Handles mobile trigger click.
 */

    const handleMobileTriggerClick = () => {
        if (!normalizedTypes.length) return;
        setPopupOpen((prev) => !prev);
    };

    /**
 * Handles mobile select type.
 *
 * @param {*} type
 */
    const handleMobileSelectType = (type) => {
        setPopupOpen(false);
        onSelectType?.(type);
    };
    if (shouldRenderCompactSelector) {

        return (
            <>
                <div className="catch-all-view-type-buttons">
                    {/* MOBILE TRIGGER */}
                    <div
                      className="catch-all-view-type-buttons-mobile-trigger"
                      style={{
                            backgroundColor: (isMobileTriggerHovered || popupOpen) ? theme.backgroundMuted : 'transparent',
                        }}
                      ref={anchorRef}
                      title={toContextualViewTitle(mobileVisibleType, viewTitlePrefix) || toDisplayLabel(mobileVisibleType)}
                      onClick={handleMobileTriggerClick}
                      onMouseEnter={() => setIsMobileTriggerHovered(true)}
                      onMouseLeave={() => setIsMobileTriggerHovered(false)}
                    >

                        {/* MOBILE TRIGGER ICON */}
                        <div className="catch-all-view-type-buttons-mobile-trigger-icon">
                            <Icon
                              iconKey={mobileVisibleIconKey}
                              width={20}
                              height={20}
                              color={hasSelectedType ? theme.foreground : theme.foregroundMuted}
                            />
                        </div>

                        {/* MOBILE TRIGGER CHEVRON */}
                        <div className="catch-all-view-type-buttons-mobile-trigger-chevron">
                            <Icon
                              iconKey="keyboard_arrow_down"
                              width={18}
                              height={18}
                              color={theme.foregroundMuted}
                            />
                        </div>
                    </div>
                </div>

                {normalizedTypes.length ? (
                    <Popup
                      anchorRef={anchorRef}
                      isOpen={popupOpen}
                      setOpen={setPopupOpen}
                    >
                        {/* MOBILE POPUP */}
                        <div
                          className="catch-all-view-type-buttons-mobile-popup"
                        >
                            {normalizedTypes.map((type) => (
                                <PopupOption
                                  key={type}
                                  iconKey={getDefaultViewTypeIconKey(type, 'table')}
                                  label={toDisplayLabel(type)}
                                  selected={hasSelectedType && normalizedSelectedType === type}
                                  onClick={() => handleMobileSelectType(type)}
                                />
                            ))}
                        </div>
                    </Popup>
                ) : null}
            </>
        );
    }

    // ----- RENDER ----- //

    return (
        <div className="catch-all-view-type-buttons">
            {normalizedTypes.map((normalizedType) => {
              return (
                    <CatchAllViewTypeButton
                      key={normalizedType}
                      iconKey={getDefaultViewTypeIconKey(normalizedType, 'table')}
                      isSelected={hasSelectedType && normalizedSelectedType === normalizedType}
                      theme={theme}
                      title={toContextualViewTitle(normalizedType, viewTitlePrefix) || normalizedType}
                      onClick={() => onSelectType?.(normalizedType)}
                    />
                );
            })}
        </div>
    );
});

// Dev Tools
CatchAllViewTypeButtons.displayName = 'CatchAllViewTypeButtons';

export default CatchAllViewTypeButtons;
